import React, { useState } from 'react'

import { Box, Flex, HStack, Icon, Image, Text, VStack, useMediaQuery } from '@chakra-ui/react'
import { device } from '@helpers/mediaQueryBreakpoints'
import { MdArrowBackIosNew, MdArrowForwardIos } from 'react-icons/md'

import * as Display from '../Display'

interface VerticalCarouselProps {
    contents: { title: string; description: string; image: string; alt: string }[]
    footer: JSX.Element
}

const VerticalCarousel = ({ contents, footer }: VerticalCarouselProps) => {
    const [current, setCurrent] = useState(0)
    const [isMobile] = useMediaQuery(device.xs)

    const [leftIsDisabled, setLeftIsDisabled] = useState<boolean>(true)
    const [rightIsDisabled, setRightIsDisabled] = useState<boolean>(false)

    const onChange = (index: number) => {
        // Update current index
        if (index >= 0 && index <= 3) {
            setCurrent(index)

            if (index >= 0 && index <= 3) {
                setCurrent(index)
                setLeftIsDisabled(index === 0)
                setRightIsDisabled(index === 3)
            }

            // Update displayed image on index change
            const imageContainer = document.getElementById('vc-img-container') as HTMLElement

            if (imageContainer) {
                imageContainer.style.transform = `translateX(${index * -100}%)`
            }
        }
    }

    const renderItem = (index: number, title: string, description: string, selected: boolean) => (
        <HStack
            key={index}
            gap={['4', '4', '6']}
            onClick={!isMobile ? () => onChange(index) : undefined}
            cursor={['none', 'pointer']}
            transition="all 0.4s"
            bg={!isMobile && selected ? 'lavender' : 'transparent'}
            borderRadius={'10'}
            padding={4}
            margin={0}
        >
            <Box transition="all 0.4s" minW="3px" h="90" bg={['green.500', selected ? 'green.500' : 'indigo.300']} />

            <VStack align="start" maxW="360">
                <Text as="h3" fontWeight="bold" fontSize={['xs', 'xs', 'sm']} color="indigo.500">
                    {title}
                </Text>

                <Text fontSize={['xs', 'xs', 'sm']} color="indigo.500">
                    {description}
                </Text>
            </VStack>
        </HStack>
    )

    return (
        <Flex w="fit-content" mx="auto" pl={['0', '32px']} pt="18px" gap="100" align="center">
            {!isMobile && (
                <Flex w="fit-content" gap="70px" align="center">
                    <Icon
                        as={MdArrowBackIosNew}
                        boxSize={6}
                        sx={{ position: 'relative', top: '-50px' }}
                        color={'grey.500'}
                        opacity={'50%'}
                        cursor={leftIsDisabled ? 'default' : 'pointer'}
                        onClick={!isMobile ? () => onChange(current - 1) : undefined}
                        visibility={leftIsDisabled ? 'hidden' : 'visible'}
                    />

                    <Display.MobileDisplay
                        transform={['none', 'scale(0.45)', 'scale(0.65)']}
                        top={['unset', '-260', '-249']}
                        media={
                            <Flex overflow="hidden">
                                <Flex id="vc-img-container" transition="all 0.4s" h={['663px']}>
                                    {contents.map((content, index) => (
                                        <Image key={index} src={content.image} h="100%" alt={content.alt} />
                                    ))}
                                </Flex>
                            </Flex>
                        }
                    />

                    <Icon
                        as={MdArrowForwardIos}
                        boxSize={6}
                        sx={{ position: 'relative', top: '-50px' }}
                        color={'grey.500'}
                        opacity={'50%'}
                        cursor={rightIsDisabled ? 'default' : 'pointer'}
                        onClick={!isMobile ? () => onChange(current + 1) : undefined}
                        visibility={rightIsDisabled ? 'hidden' : 'visible'}
                    />
                </Flex>
            )}

            <Flex direction="column" gap="2">
                {contents.map((content, index) =>
                    renderItem(index, content.title, content.description, index === current)
                )}

                {footer}
            </Flex>
        </Flex>
    )
}

export default VerticalCarousel
